import { Alert, AlertTitle, Box, LinearProgress } from "@mui/material";
import type { FC } from "react";
import { useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { SupabaseEventService } from "../../modules/event/server/supabase-event-service";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { FilterMoments } from "../../modules/moment-filter/components/filter-moments";
import { MomentFilter } from "../../modules/moment-filter/components/moment-filter";
import { SupabaseMomentService } from "../../modules/moment/server/supabase-moment-service";
import { TimelineEngine } from "../../modules/timeline/components/timeline-engine";
import { noNullFields } from "../../server/supabase/base-supabase-service";
import { useSupabaseLoader } from "../../server/supabase/hooks";
import { SelectEvent } from "./select-event";

const TimelinePage: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { loading, data, error } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const [momentsResp, eventKeysResp] = await Promise.all([
        new SupabaseMomentService(supabase).getEventMomentCardMoments(account_id, id as string),
        new SupabaseEventService(supabase).getKeys({ includeLibrary: false }),
      ]);

      if (momentsResp.error || eventKeysResp.error) {
        console.error("Error loading timeline data", momentsResp.error, eventKeysResp.error);
        throw new Error("Failed to load data");
      }

      return {
        moments: momentsResp.data,
        eventKeys: noNullFields(eventKeysResp.data)
          ? eventKeysResp.data.filter((e) => !e.calendar || e.has_schedule_event)
          : [],
      };
    },
    [id],
  );

  const handleChange = useCallback(
    (key: string) => {
      void navigate(`/timeline/${key}?${searchParams.toString()}`);
    },
    [navigate, searchParams],
  );

  return (
    <Page title="Timeline | ChangeEngine">
      <PageHeader>
        <PageHeading
          heading="Timeline"
          subheading="All the Communications connected to a specific Event"
        ></PageHeading>
      </PageHeader>
      <PageContent>
        {loading && <LinearProgress />}
        {!loading && (
          <Box display="flex" mt={2} gap={2}>
            <Box flexBasis={300}>
              {data?.moments && (
                <SelectEvent
                  id={id}
                  eventKeys={data.eventKeys}
                  onChange={handleChange}
                  label="Event"
                />
              )}
            </Box>
            <MomentFilter buttonProps={{ sx: { p: 2 } }} />
          </Box>
        )}
        {error && (
          <Alert severity="error">
            <AlertTitle>Something went wrong</AlertTitle>
          </Alert>
        )}
        {data?.moments && (
          <FilterMoments moments={data.moments}>
            {(filtered) => <TimelineEngine moments={filtered} />}
          </FilterMoments>
        )}
      </PageContent>
    </Page>
  );
};

export default TimelinePage;
